/** Musical race rules. Everything here uses round time, including muted clients. */
import * as pmath from './pmath.js';
import type {RaceCourse,RaceVec3,RaceBox} from './raceCourse.js';
import type {RacePlayerState,RacePose,RaceServerMsg} from './raceProtocol.js';
import type {RaceTarget} from './raceTargets.js';
import {raceRelayPhase,raceBossVulnerable,raceBossFiringZone,raceInsideAttackZone,hitRaceBossZone,RACE_CORE_HP,RACE_EMITTER_HP,type RaceAttackZone} from './raceBoss.js';
export {raceCoreOpen,RACE_CORE_HP,RACE_EMITTER_HP} from './raceBoss.js';

export interface RaceLaunchPad {id:string;center:RaceVec3;forward:RaceVec3;radius:number}
export interface RaceRhythm {bpm:number;pads:RaceLaunchPad[]}
export interface RaceArena {
 center:RaceVec3;forward:RaceVec3;right:RaceVec3;spawn:RaceVec3;rotation:[number,number,number,number];
 emitters:string[];core:string;zones:RaceAttackZone[];
}
export interface RaceWarp {startedAt:number;from:RaceVec3;velocity?:RaceVec3;rotation?:[number,number,number,number]}
export interface RaceEncounterState {entered:boolean;emitters:number[];core:number;lastWave:number;lastStrike?:number;warp?:RaceWarp}
export const RACE_WARP_MS=6500;
export function racePlayerDone(course:Pick<RaceCourse,'arena'>,player:Pick<RacePlayerState,'finishedMs'|'encounter'>):boolean{
 return player.finishedMs!==null&&(!course.arena||!!player.encounter?.entered&&player.encounter.core<=0);
}
export function racePlayerCanMove(course:Pick<RaceCourse,'arena'>,player:Pick<RacePlayerState,'finishedMs'|'encounter'|'dnf'>):boolean{
 return !player.dnf&&!player.encounter?.warp&&!racePlayerDone(course,player);
}
export function copyRaceEncounter(s:RaceEncounterState|undefined):RaceEncounterState|undefined{
 return s?{...s,emitters:[...s.emitters],...(s.warp?{warp:{...s.warp,from:[...s.warp.from],velocity:s.warp.velocity?[...s.warp.velocity]:undefined,rotation:s.warp.rotation?[...s.warp.rotation]:undefined}}:{})}:undefined;
}
const ease=(t:number)=>{t=Math.max(0,Math.min(1,t));return t*t*t*(t*(t*6-15)+10);};
function blendRotation(a:readonly number[],b:readonly number[],t:number):[number,number,number,number]{
 const sign=a.reduce((sum,v,i)=>sum+v*b[i],0)<0?-1:1,q=a.map((v,i)=>v*(1-t)+b[i]*sign*t),length=Math.sqrt(q.reduce((sum,v)=>sum+v*v,0))||1;
 return q.map(v=>v/length) as [number,number,number,number];
}
/** Quintic flight preserves entry momentum and has zero velocity/acceleration
 * at touchdown. Facing follows its tangent, with continuous entry/exit turns. */
export function sampleRaceWarp(course:RaceCourse,warp:RaceWarp,now:number){
 const a=course.arena!,t=Math.max(0,Math.min(1,(now-warp.startedAt)/RACE_WARP_MS)),seconds=RACE_WARP_MS/1000;
 const t2=t*t,t3=t2*t,t4=t3*t,t5=t4*t,tail=1-t;
 const u=ease(t),du=30*t2*tail*tail,carry=t-6*t3+8*t4-3*t5,dCarry=1-18*t2+32*t3-15*t4;
 const lift=180*64*t3*tail*tail*tail,dLift=180*192*t2*tail*tail*(1-2*t),initial=warp.velocity??[0,0,0];
 const p=warp.from.map((v,k)=>v+(a.spawn[k]-v)*u+initial[k]*seconds*carry+(k===1?lift:0)) as RaceVec3;
 const v=warp.from.map((n,k)=>(a.spawn[k]-n)*du/seconds+initial[k]*dCarry+(k===1?dLift/seconds:0)) as RaceVec3;
 const heading=pmath.hypot(v[0],v[2])>.001?v:a.forward,yaw=pmath.atan2(-heading[0],-heading[2]);
 const tangent=[0,pmath.sin(yaw/2),0,pmath.cos(yaw/2)],entry=blendRotation(warp.rotation??tangent,tangent,ease(t*seconds/.7));
 const q=blendRotation(entry,a.rotation,ease((t*seconds-(seconds-.65))/.65));
 return {p,v,q,progress:t};
}
export function startRaceWarp(course:RaceCourse,player:RacePlayerState,now:number,startsAt:number):boolean{
 if(player.finishedMs!==null||player.gate!==course.gates.length)return false;
 player.finishedMs=Math.max(0,now-startsAt);
 if(course.arena){player.encounter??=newRaceEncounter();player.encounter.warp={startedAt:now,from:[...player.pose.p],velocity:[...player.pose.v],rotation:[...player.pose.q]};}
 else player.pose={...player.pose,v:[0,0,0],j:0,fp:0,am:false};
 return true;
}
export function advanceRaceWarp(course:RaceCourse,player:RacePlayerState,now:number):Extract<RaceServerMsg,{t:'raceEnergy'}>|null{
 const warp=player.encounter?.warp;if(!warp)return null;
 const flight=sampleRaceWarp(course,warp,now),arrived=flight.progress>=1;
 player.pose={...player.pose,p:flight.p,q:arrived?[...course.arena!.rotation]:flight.q,v:flight.v,j:arrived?0:2,air:!arrived,fp:arrived?0:1,am:false,wr:0,lc:0,fl:0,dp:0,fa:0,mf:0,mr:0,cl:flight.v[1],jumpChain:0,blade:undefined};
 if(!arrived)return null;
 delete player.encounter!.warp;player.encounter!.entered=true;
 const grant=100-player.pose.energy;player.pose.energy=100;player.pose.energyRevision=(player.pose.energyRevision??0)+1;
 return {t:'raceEnergy',playerId:player.id,attempt:player.attempt,energy:100,grant,energyRevision:player.pose.energyRevision,reason:'arena'};
}
export const newRaceEncounter=():RaceEncounterState=>({entered:false,emitters:[RACE_EMITTER_HP,RACE_EMITTER_HP,RACE_EMITTER_HP],core:RACE_CORE_HP,lastWave:-1,lastStrike:-1});
export const raceBeat=(course:Pick<RaceCourse,'rhythm'>,time:number)=>Math.max(0,time)*(course.rhythm?.bpm??120)/60;
export const raceBeatDistance=(course:Pick<RaceCourse,'rhythm'>,time:number,offset=0)=>{
 const beat=raceBeat(course,time)-offset;return Math.abs(beat-Math.round(beat))*60/(course.rhythm?.bpm??120);
};
export function raceLaunchImpulse(course:RaceCourse,position:readonly number[],time:number):RaceVec3|null{
 const pad=course.rhythm?.pads.find(p=>Math.abs(position[1]-p.center[1])<.45&&pmath.hypot(position[0]-p.center[0],position[2]-p.center[2])<p.radius);
 if(!pad)return null;
 const perfect=raceBeatDistance(course,time)<.105,up=perfect?6:2,forward=perfect?5:1.5;
 return [pad.forward[0]*forward,up,pad.forward[2]*forward];
}
export function raceTargetOpen(course:Pick<RaceCourse,'rhythm'|'arena'>,target:RaceTarget,time:number,player?:Pick<RacePlayerState,'encounter'|'pose'>):boolean{
 if(target.kind==='percussion')return raceBeatDistance(course,time,target.beatOffset??0)<.2;
 if(target.kind!=='emitter'&&target.kind!=='core')return true;
 const zone=raceBossFiringZone(course,target.id,time);
 return raceBossVulnerable(course,target,time,player?.encounter)&&!!zone&&!!player&&raceInsideAttackZone(zone,player.pose.p);
}
export function raceAvailableTargets(course:RaceCourse,player:Pick<RacePlayerState,'pose'|'encounter'|'targetRespawns'>,time:number,nowMs:number):Set<string>{
 return new Set(course.targets.filter(t=>(player.targetRespawns[t.id]??0)<=nowMs&&raceTargetOpen(course,t,time,player)).map(t=>t.id));
}
/** Mutation is shared by the server and the offline authority. No client-supplied HP. */
export function damageRaceTarget(course:RaceCourse,player:RacePlayerState,targetId:string,damage:number,time:number):{grant:number;cooldown:number;destroyed:boolean}|null{
 const target=course.targets.find(t=>t.id===targetId);if(!target||!raceTargetOpen(course,target,time,player))return null;
 const state=player.encounter,arena=course.arena;
 if(state&&arena&&(target.kind==='emitter'||target.kind==='core')){
  if(!Number.isFinite(damage)||damage<=0)return null;
  let left:number;
  if(target.kind==='core')left=state.core=Math.max(0,state.core-damage);
  else {const i=arena.emitters.indexOf(targetId);left=state.emitters[i]=Math.max(0,state.emitters[i]-damage);}
  return {grant:left<=0?25:3,cooldown:left<=0?1e9:.045,destroyed:left<=0};
 }
 const perfect=target.kind==='percussion'&&raceBeatDistance(course,time,target.beatOffset??0)<.075;
 return {grant:perfect?35:25,cooldown:8,destroyed:true};
}
export function raceEncounterMovement(course:RaceCourse,player:RacePlayerState,before:RacePose,start:number,end:number):Extract<RaceServerMsg,{t:'raceEnergy'}>[] {
 const events:Extract<RaceServerMsg,{t:'raceEnergy'}>[]=[];
 const change=(grant:number,reason:'arena'|'wave'|'strike',impulse?:RaceVec3)=>{
  player.pose.energy=Math.max(0,Math.min(100,player.pose.energy+grant));player.pose.energyRevision=(player.pose.energyRevision??0)+1;
  events.push({t:'raceEnergy',playerId:player.id,attempt:player.attempt,energy:player.pose.energy,grant,energyRevision:player.pose.energyRevision,reason,...(impulse?{impulse}:{})});
 };
 const wave=hitRaceBassWave(course,player.encounter,before.p,player.pose.p,start,end);
 if(wave!==null){
  player.encounter!.lastWave=wave;const center=course.arena!.center,x=player.pose.p[0]-center[0],z=player.pose.p[2]-center[2],length=pmath.hypot(x,z)||1;
  change(-12,'wave',[x/length*6,2,z/length*6]);
 }
 const strike=hitRaceBossZone(course,player.encounter,before.p,player.pose.p,start,end);
 if(strike!==null){player.encounter!.lastStrike=strike;const a=course.arena!,dx=player.pose.p[0]-a.center[0],dz=player.pose.p[2]-a.center[2],length=pmath.hypot(dx,dz)||1;change(-24,'strike',[dx/length*9,3,dz/length*9]);}
 return events;
}
export function raceCheckpoint(course:RaceCourse,player:RacePlayerState,reason:string):{p:RaceVec3;q:[number,number,number,number];checkpoint:boolean}{
 const checkpoint=reason!=='start'&&reason!=='restart'&&!!course.arena&&!!player.encounter?.entered;
 if(!checkpoint){player.finishedMs=null;player.encounter=newRaceEncounter();for(const t of course.targets)if(t.kind==='emitter'||t.kind==='core')delete player.targetRespawns[t.id];}
 player.gate=checkpoint?course.gates.length:0;
 return {p:[...(checkpoint?course.arena!.spawn:course.spawns[player.spawn])],q:[...(checkpoint?course.arena!.rotation:course.spawnRotation)],checkpoint};
}

export interface RaceBassWave {id:number;radius:number;warning:boolean;active:boolean}
export function raceBassWave(course:RaceCourse,time:number):RaceBassWave{
 const beat=raceBeat(course,time),phase=beat%8;
 return {id:Math.floor(beat/8),radius:4+Math.max(0,phase-2)*8,warning:phase<2,active:phase>=2&&phase<7};
}
export function raceArenaWalls(course:Pick<RaceCourse,'arena'|'rhythm'>,time:number):RaceBox[]{
 const arena=course.arena;if(!arena)return [];
 // Sliding port armor is real geometry shared by movement and projectiles.
 return arena.emitters.map((id,i)=>{
  const zone=arena.zones.find(z=>z.targetId===id)!,dx=zone.center[0]-arena.center[0],dz=zone.center[2]-arena.center[2],length=pmath.hypot(dx,dz),x=dx/length,z=dz/length;
  const phase=raceRelayPhase(course,i,time).phase,open=phase<8?ease((phase-7.2)/.8):1-ease((phase-14)/.8);
  return {id:`conductor-port-${i}`,kind:'wall',center:[arena.center[0]+x*23.45,arena.center[1]+2.675+open*1.5,arena.center[2]+z*23.45],size:[4.8,.95,.45],yaw:pmath.atan2(-x,-z),standable:false};
 });
}
export function raceConductorBeam(course:RaceCourse,time:number){
 const beat=raceBeat(course,time),phase=beat%8,angle=beat*.22;
 return {id:Math.floor(beat/8)*2+1,angle,warning:phase>=5&&phase<6,active:phase>=6};
}
/** Swept ring/player intersection in small clock slices; jumping and flight
 * clear the low wave. Contact costs momentum and energy, never repeats per wave. */
export function hitRaceBassWave(course:RaceCourse,state:RaceEncounterState|undefined,from:RaceVec3,to:RaceVec3,start:number,end:number):number|null{
 const arena=course.arena;if(!arena||!state?.entered||state.core<=0||end<start)return null;
 const steps=Math.max(1,Math.ceil(Math.min(.5,end-start)/.02));
 for(let i=0;i<=steps;i++){
  const f=i/steps,time=start+(end-start)*f,wave=raceBassWave(course,time);
  const p=from.map((v,k)=>v+(to[k]-v)*f),height=p[1]-arena.center[1];
  if(height<-.5||height>.65)continue;
  const radius=pmath.hypot(p[0]-arena.center[0],p[2]-arena.center[2]);
  // Covers travel between samples, including the fastest legal racer.
  const padding=.65+pmath.hypot(...to.map((v,k)=>v-from[k]) as RaceVec3)/steps/2+8*(course.rhythm?.bpm??120)/60*(end-start)/steps/2;
  if(wave.active&&wave.id*2>state.lastWave&&radius<40&&Math.abs(radius-wave.radius)<padding)return wave.id*2;
  const beam=raceConductorBeam(course,time),dx=p[0]-arena.center[0],dz=p[2]-arena.center[2];
  const along=dx*pmath.cos(beam.angle)+dz*pmath.sin(beam.angle),across=-dx*pmath.sin(beam.angle)+dz*pmath.cos(beam.angle);
  if(beam.active&&beam.id>state.lastWave&&along>6&&along<39&&Math.abs(across)<padding)return beam.id;
 }
 return null;
}
